import { useCallback, useMemo } from "react";
import { useLocation } from "react-router";
import type { GetOrganizationsResult } from "@src/application/useCases/organization/GetOrganizations.useCase";
import {
  GetOrganizationDetailsUseCase,
  GetOrganizationsUseCase,
} from "@src/application/useCases/organization";
import { GetOrganizationsDTO } from "@src/domain/dto/organizations";
import type { Organization } from "@src/domain/entities/organization";
import { GraphQlAdapter } from "@src/infrastructure/adapters/graphQl/GraphQl.adapter";
import { OrganizationRepository } from "@src/infrastructure/repositories/organization";
import {
  useFetchOrganizationDetails,
  useFetchOrganizationsData,
  useOrganizationsData,
} from "@src/presentation/providers/OrganizationsDataProvider";
import {
  isUsableOrganization,
  organizationKeysMatch,
  readOrganizationIdForKey,
} from "./organizationDetailsResolve";

export type OrganizationRouteFetchContext = {
  listData: GetOrganizationsResult | null;
  locationState: unknown;
  organizationDetails: Organization | null;
  organizationDetailsKey: string | null;
  getCachedDetails: (key: string) => Organization | null;
};

export function listHasOrganizationKey(
  listData: GetOrganizationsResult | null,
  organizationKey: string,
): boolean {
  return Boolean(
    listData?.items?.some((org) =>
      organizationKeysMatch(org.organizationKey, organizationKey),
    ),
  );
}

export async function fetchOrganizationsListForRoute(
  options?: { refetch?: boolean },
): Promise<GetOrganizationsResult> {
  const payload = { page_number: 1, page_size: 100 };
  const validated = GetOrganizationsDTO.validate(payload);
  if (!validated.success) {
    throw new Error("GetOrganizations validation failed");
  }
  const repo = new OrganizationRepository(
    GraphQlAdapter.getInstance().getClient(),
  );
  return new GetOrganizationsUseCase(repo).execute(
    new GetOrganizationsDTO(validated.data),
    options?.refetch,
  );
}

export function resolveOrganizationIdForRouteKey(
  key: string,
  ctx: OrganizationRouteFetchContext,
): number | null {
  const fromState = (ctx.locationState as { organizationId?: number })
    ?.organizationId;
  let organizationId: number | null =
    typeof fromState === "number" ? fromState : null;

  organizationId ??= readOrganizationIdForKey(key);

  if (
    organizationId == null &&
    ctx.organizationDetails &&
    (ctx.organizationDetailsKey === key ||
      organizationKeysMatch(ctx.organizationDetails.organizationKey, key))
  ) {
    organizationId ??= ctx.organizationDetails.organizationId;
  }

  if (organizationId == null && ctx.listData?.items?.length) {
    const found = ctx.listData.items.find((org) =>
      organizationKeysMatch(org.organizationKey, key),
    );
    organizationId ??= found?.organizationId ?? null;
  }

  return organizationId;
}

export async function fetchOrganizationByRouteKey(
  key: string,
  ctx: OrganizationRouteFetchContext,
): Promise<Organization | null> {
  const cached = ctx.getCachedDetails(key);
  if (isUsableOrganization(cached)) return cached;

  const organizationId = resolveOrganizationIdForRouteKey(key, ctx);
  if (organizationId == null) return null;

  const repo = new OrganizationRepository(
    GraphQlAdapter.getInstance().getClient(),
  );
  const result = await new GetOrganizationDetailsUseCase(repo).execute(
    organizationId,
  );
  return result.organization;
}

/**
 * Loads organizations list (for id resolution) and organization details for a route key.
 * Shared by OrganizationDetailsPage and BrandDetails.
 */
export function useOrganizationRouteFetch(
  organizationKey: string | undefined,
): void {
  const location = useLocation();
  const {
    data: listData,
    organizationDetails,
    organizationDetailsKey,
    getCachedDetails,
  } = useOrganizationsData();

  const resolveListFetchFn = useCallback(
    async (options?: { refetch?: boolean }) => {
      if (
        !options?.refetch &&
        organizationKey &&
        listHasOrganizationKey(listData, organizationKey)
      ) {
        return listData;
      }
      return fetchOrganizationsListForRoute(options);
    },
    [organizationKey, listData],
  );

  useFetchOrganizationsData(resolveListFetchFn, [
    organizationKey ?? "",
    listData?.items?.length ?? 0,
  ]);

  const fetchContext = useMemo(
    (): OrganizationRouteFetchContext => ({
      listData,
      locationState: location.state,
      organizationDetails,
      organizationDetailsKey,
      getCachedDetails,
    }),
    [
      listData,
      location.state,
      organizationDetails,
      organizationDetailsKey,
      getCachedDetails,
    ],
  );

  const fetchDetailsFn = useCallback(
    async (key: string) => fetchOrganizationByRouteKey(key, fetchContext),
    [fetchContext],
  );

  useFetchOrganizationDetails(fetchDetailsFn, organizationKey ?? undefined);
}
